import LockOpenIcon from "@mui/icons-material/LockOpen";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../services/auth-api";
import { useAuthStore } from "../../store/auth-store";

const demoAccounts = [
  { email: "admin@seed-ims.gd", password: "Admin123!", role: "System Administrator", description: "Full platform access, user management, system configuration", color: "#2e7d32" },
  { email: "manager@seed-ims.gd", password: "Password123!", role: "Case Manager", description: "Case management, participant intake, task assignments", color: "#1565c0" },
  { email: "auditor@seed-ims.gd", password: "Password123!", role: "Auditor", description: "Audit logs, compliance monitoring, read-only oversight", color: "#6a1b9a" },
  { email: "treasury@seed-ims.gd", password: "Password123!", role: "Treasury Officer", description: "Paysheet verification, financial oversight", color: "#e65100" },
  { email: "auth-officer@seed-ims.gd", password: "Password123!", role: "Authorizing Officer", description: "Final paysheet authorization, disbursement approval", color: "#c62828" },
  { email: "citizen@seed-ims.gd", password: "Password123!", role: "Citizen", description: "View own cases, submit applications", color: "#00838f" },
];

export const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const setSession = useAuthStore((state) => state.setSession);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.login({ email, password });
      setSession(response);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign in");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Left panel - branding */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          width: "45%",
          bgcolor: "#1b5e20",
          color: "white",
          p: 6,
        }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          SEED IMS
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4, opacity: 0.9 }}>
          Integrated Social Services Platform
        </Typography>
        <Typography variant="h3" fontWeight="bold" sx={{ mb: 3, lineHeight: 1.2 }}>
          Social Services Management for Grenada
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, opacity: 0.85, maxWidth: 480 }}>
          Comprehensive case management, benefit disbursement, and program tracking for SEED, BRISP, Youth Skills Development, and Senior Support Services.
        </Typography>
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
          {["Case management & participant tracking", "Multi-stage paysheet approval workflow", "Role-based access for 6 user types", "Full audit trail & compliance logging"].map((item) => (
            <Typography key={item} component="li" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#81c784", flexShrink: 0 }} />
              {item}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Right panel - login form + demo accounts */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", p: 4, overflow: "auto" }}>
        <Container maxWidth="sm">
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
            <Avatar sx={{ m: 1, bgcolor: "#1b5e20", width: 48, height: 48 }}>
              <LockOpenIcon />
            </Avatar>
            <Typography component="h1" variant="h5" fontWeight="bold">
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to access the SEED IMS platform
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            {error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ) : null}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, bgcolor: "#1b5e20", "&:hover": { bgcolor: "#2e7d32" }, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
            </Button>
            <Link component="button" variant="body2" onClick={() => navigate("/auth/forgot-password")} disabled>
              Forgot password? (coming soon)
            </Link>
          </Box>

          {/* Demo Accounts */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Demo Accounts (click to autofill)
            </Typography>
            <Grid container spacing={1.5}>
              {demoAccounts.map((account) => (
                <Grid item xs={12} key={account.email}>
                  <Card
                    onClick={() => handleDemoLogin(account.email, account.password)}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      border: "1px solid",
                      borderColor: email === account.email ? account.color : "divider",
                      bgcolor: email === account.email ? `${account.color}08` : "transparent",
                      boxShadow: "none",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: account.color, bgcolor: `${account.color}08` },
                    }}
                  >
                    <Avatar sx={{ bgcolor: account.color, width: 36, height: 36, fontSize: 14, fontWeight: "bold" }}>
                      {account.role.charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {account.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium">
                        {account.role}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {account.description}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
